import ApiService from '../../components/api_services/apiService.jsx';
import {
  setRowsUserReports,
  setMaxPagesUserReports,
  setPageUserReports,
} from '../../store/userReportsTabSlice.js';

const PAGE_SIZE = 20;

const formatAddedAt = (value) => {
  if (!value) {
    return '';
  }

  const [datePart, timePart = ''] = value.split('T');
  return `${datePart} ${timePart.substring(0, 5)}`.trim();
};

const getMessageEndpointKeyByType = (contentType) => {
  const normalizedType = (contentType || '').toUpperCase();

  if (normalizedType === 'GROUP') {
    return 'chat';
  }

  return 'direct';
};

const normalizeMessagesResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.content)) {
    return response.content;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

export const fetchUserReports = async (currentPage, dispatch) => {
  try {
    const pageIndex = Math.max(0, currentPage - 1);
    const response = await ApiService.getNotResolvedUserReports(pageIndex, PAGE_SIZE);
    const reports = response?.content || [];

    const totalPages = Math.max(response?.totalPages || 1, 1);
    dispatch(setMaxPagesUserReports(totalPages));

    if (currentPage > totalPages) {
      dispatch(setPageUserReports(totalPages));
      return;
    }

    const userIds = [...new Set(
      reports.flatMap((report) => [report.reportedBy, report.reportedUser]).filter(Boolean)
    )];

    const profilesEntries = await Promise.all(
      userIds.map(async (userId) => {
        try {
          const profile = await ApiService.getProfileById(userId);
          return [userId, profile];
        } catch (error) {
          return [userId, null];
        }
      })
    );

    const profilesMap = Object.fromEntries(profilesEntries);

    const directMessageIds = new Set();
    const chatMessageIds = new Set();

    reports.forEach((report) => {
      const reportContentIds = Array.isArray(report.reportContent) ? report.reportContent : [];
      const endpointKey = getMessageEndpointKeyByType(report.contentType);

      reportContentIds.forEach((messageId) => {
        if (!messageId) {
          return;
        }

        if (endpointKey === 'chat') {
          chatMessageIds.add(messageId);
        } else {
          directMessageIds.add(messageId);
        }
      });
    });

    const [directMessagesRaw, chatMessagesRaw] = await Promise.all([
      directMessageIds.size > 0 ? ApiService.getMessagesByIds(Array.from(directMessageIds)) : Promise.resolve([]),
      chatMessageIds.size > 0 ? ApiService.getChatMessagesByIds(Array.from(chatMessageIds)) : Promise.resolve([]),
    ]);

    const directMessages = normalizeMessagesResponse(directMessagesRaw);
    const chatMessages = normalizeMessagesResponse(chatMessagesRaw);

    const messagesMap = {};
    [...(directMessages || []), ...(chatMessages || [])].forEach((message) => {
      if (message?.id) {
        messagesMap[message.id] = message;
      }
    });

    const mappedRows = reports.map((report) => ({
      endpointKey: getMessageEndpointKeyByType(report.contentType),
      id: report.id,
      reportedBy: report.reportedBy || '',
      reportedUser: report.reportedUser || '',
      reportedByProfile: profilesMap[report.reportedBy] || null,
      reportedUserProfile: profilesMap[report.reportedUser] || null,
      reportDescription: report.reportDescription || '',
      contentType: report.contentType || '',
      reportContentIds: Array.isArray(report.reportContent) ? report.reportContent : [],
      reportMessages: (Array.isArray(report.reportContent) ? report.reportContent : [])
        .map((messageId) => messagesMap[messageId])
        .filter(Boolean),
      addedAt: formatAddedAt(report.addedAt),
    }));

    dispatch(setRowsUserReports(mappedRows));
  } catch (error) {
    console.error('Błąd podczas pobierania zgłoszeń użytkowników:', error);
    dispatch(setRowsUserReports([]));
    dispatch(setMaxPagesUserReports(1));
  }
};
